import { forwardRef, useCallback, useMemo } from 'react';

import { Button, type ButtonProps } from '../../../atoms';
import { useVirtualMultiselectRoot } from '../contexts/root.context';

interface VirtualMultiselectToggleButtonProps
  extends Omit<ButtonProps, 'children'> {
  children: (state: 'all' | 'none') => React.ReactNode;
}

export const VirtualMultiselectToggleButton = forwardRef<
  HTMLButtonElement,
  VirtualMultiselectToggleButtonProps
>(({ children, onClick, ...props }, ref) => {
  const { value, onValueChange, availableItemsSet } =
    useVirtualMultiselectRoot();

  const allSelected = useMemo(
    () =>
      value.length === availableItemsSet.size &&
      value.every((id) => availableItemsSet.has(id)),
    [value, availableItemsSet],
  );

  const state = allSelected ? 'all' : 'none';

  const handleToggleAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (allSelected) {
        onValueChange([]);
      } else {
        onValueChange(Array.from(availableItemsSet));
      }
      onClick?.(e);
    },
    [allSelected, onValueChange, availableItemsSet, onClick],
  );

  return (
    <Button ref={ref} onClick={handleToggleAll} {...props}>
      {children(state)}
    </Button>
  );
});
